import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { isFcmClientEnabled } from "../../config/firebase";
import { canRegisterFcmForUser } from "../../helpers/fcm-role";
import {
  registerFcmTokenIfPermitted,
  refreshFcmTokenForSession,
  subscribeFcmForeground,
  getFcmNotificationPermission,
} from "../../services/fcm.service";
import FcmEnableNotificationsPrompt from "./FcmEnableNotificationsPrompt";

/**
 * Observes auth session and manages FCM token lifecycle.
 * Does not request notification permission automatically — user must click Enable.
 * Does not modify Socket.IO or unread badge behavior.
 */
export default function FcmBootstrap() {
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const authInfo = useSelector((state) => state.auth.authInfo);
  const userInfo = useSelector((state) => state.auth.userInfo);
  const lastRegisteredRef = useRef(null);
  const [permissionVersion, setPermissionVersion] = useState(0);

  useEffect(() => {
    if (!isFcmClientEnabled()) {
      return undefined;
    }

    return subscribeFcmForeground((payload) => {
      const body =
        payload.notification?.body ||
        payload.data?.body ||
        "FCM foreground message received";
      console.log("[FCM] foreground message", payload);
      toast.info(body, { toastId: "fcm-foreground-test" });
    });
  }, []);

  useEffect(() => {
    if (!isFcmClientEnabled()) {
      return undefined;
    }
    if (!isLoggedIn || !authInfo?.token || !authInfo?.id) {
      lastRegisteredRef.current = null;
      return undefined;
    }
    if (!canRegisterFcmForUser(userInfo)) {
      return undefined;
    }
    if (getFcmNotificationPermission() !== "granted") {
      return undefined;
    }

    const sessionKey = `${authInfo.id}:${userInfo.role}`;
    if (lastRegisteredRef.current === sessionKey) {
      return undefined;
    }

    let cancelled = false;
    registerFcmTokenIfPermitted(authInfo, userInfo).then((result) => {
      if (!cancelled && result?.success) {
        lastRegisteredRef.current = sessionKey;
        console.log("[FCM] token registered for session");
      }
    });

    return () => {
      cancelled = true;
    };
  }, [isLoggedIn, authInfo, userInfo, permissionVersion]);

  useEffect(() => {
    if (!isFcmClientEnabled() || !isLoggedIn || !authInfo?.token) {
      return undefined;
    }
    if (!canRegisterFcmForUser(userInfo)) {
      return undefined;
    }
    if (getFcmNotificationPermission() !== "granted") {
      return undefined;
    }

    const onVisible = () => {
      if (document.visibilityState === "visible") {
        refreshFcmTokenForSession(authInfo, userInfo);
      }
    };

    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [isLoggedIn, authInfo, userInfo, permissionVersion]);

  return (
    <FcmEnableNotificationsPrompt
      onEnabled={() => {
        if (authInfo?.id && userInfo?.role) {
          lastRegisteredRef.current = `${authInfo.id}:${userInfo.role}`;
        }
        setPermissionVersion((v) => v + 1);
      }}
    />
  );
}
